"use client"

import React, { useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import { GitBranch, Loader2 } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { FieldMappingEditor } from "@/components/field-mapping-editor"
import { useMappingConfig } from "@/hooks/use-mapping-config"
import { useAtsSource } from "@/contexts/ats-source-context"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

interface MappingSummary {
  /** ATS entities the mapping reads, e.g. "companies" or "Candidate, CandidateWorkHistory" */
  entities: string
  mapped: number
}

/**
 * The Mappings tool, laid out like the explorer: mapping types in a left rail, the
 * selected mapping's editor on the canvas. The selection lives in `?mappingName=` so
 * it survives a refresh and the browser back button.
 */
export function MappingsPage({ mappingName }: { mappingName: string | null }) {
  const router = useRouter()
  const { t, n } = useTranslation()
  const { loadMappingNames, loadConfig } = useMappingConfig()
  const { hasNoSources, selectedSource } = useAtsSource()
  const [names, setNames] = useState<string[]>([])
  const [summaries, setSummaries] = useState<Record<string, MappingSummary>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    loadMappingNames()
      .then((loaded) => {
        if (!cancelled) setNames(loaded)
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : t("mappings.errors.loadNamesFailed")))
      .finally(() => !cancelled && setIsLoading(false))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sub-lines for the rail: the ATS entities read and how many fields are mapped, per source
  useEffect(() => {
    if (names.length === 0 || hasNoSources) return
    let cancelled = false
    setSummaries({})
    names.forEach((name) => {
      loadConfig(name, selectedSource?.code)
        .then((config) => {
          if (cancelled) return
          const mapped = config.entities.reduce((n, entity) => n + entity.fieldMappings.length, 0)
          const entities = [...new Set(config.entities.map((entity) => entity.entityType || entity.name))].join(", ") || config.atsEntityType
          setSummaries((current) => ({ ...current, [name]: { entities, mapped } }))
        })
        .catch(() => {
          // The rail still lists the mapping; the editor reports the load error
        })
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [names, selectedSource?.code, hasNoSources])

  const selected = mappingName && mappingName !== "selector" ? mappingName : null

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {error && (
        <Alert variant="destructive" className="mx-4 mt-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="mx-4 mb-4 mt-3 flex min-h-0 flex-1 overflow-hidden rounded-lg border border-card-border bg-card shadow-card">
        <aside className="flex h-full w-[280px] shrink-0 flex-col border-r border-card-border bg-card">
          <div className="border-b border-card-border px-4 py-3">
            <div className="th-section-label">{t("mappings.rail.heading")}{names.length > 0 ? ` · ${n(names.length)}` : ""}</div>
            <p className="mt-0.5 text-[11px] text-muted-foreground">{t("mappings.rail.subheading")}</p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {isLoading ? (
              <div className="flex items-center p-2 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("common.state.loading")}
              </div>
            ) : (
              names.map((name) => {
                const summary = summaries[name]
                const active = name === selected
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => router.push(`?mappingName=${encodeURIComponent(name)}`)}
                    className={`mb-1 w-full rounded-md px-3 py-2 text-left text-sm hover:bg-row-hover ${active ? "bg-accent font-semibold text-accent-foreground" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{name}</span>
                      {summary && (
                        <span className={`shrink-0 rounded-pill px-1.5 py-0.5 text-[10px] font-bold ${summary.mapped > 0 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                          {n(summary.mapped)}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 truncate text-[11px] font-normal text-muted-foreground">
                      {summary
                        ? t("mappings.rail.reads", { entities: summary.entities || t("mappings.rail.noEntities") })
                        : hasNoSources
                          ? t("mappings.rail.noSource")
                          : "…"}
                    </div>
                  </button>
                )
              })
            )}
            {!isLoading && names.length === 0 && <p className="px-3 py-2 text-xs text-muted-foreground">{t("mappings.rail.empty")}</p>}
          </div>
        </aside>

        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
          {hasNoSources ? (
            <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
              {t("mappings.empty.noSources")}
            </div>
          ) : selected ? (
            <FieldMappingEditor key={selected} mappingName={selected} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-muted-foreground">
              <GitBranch className="h-6 w-6 text-primary" />
              <p className="font-display text-base font-bold text-foreground">{t("mappings.empty.title")}</p>
              <p className="max-w-sm text-sm">{t("mappings.empty.description")}</p>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}
